import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { addDays, addHours, endOfDay, format, startOfDay } from "date-fns";
import { es } from "date-fns/locale";
import { AlertTriangle, ArrowRightLeft, Camera, Check, Hand, Loader2, MapPin, Search, Undo2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { getApiErrorMessage, getMaterialPublic, getMaterialPublicFotoUrl, materialPublicCoger, materialPublicDevolver } from "@/lib/api";
import { MATERIAL_ESTADOS, formatReservaRango, formatVuelve } from "@/lib/material";
import { MaterialEstadoBadge } from "@/components/material/MaterialShared";
import { UserAvatar } from "@/components/UserAvatar";

const QUIEN_KEY = "material_quien";

function leerQuien() {
  try {
    return localStorage.getItem(QUIEN_KEY) || null;
  } catch (_) {
    return null;
  }
}

function guardarQuien(id) {
  try {
    if (id) localStorage.setItem(QUIEN_KEY, id);
    else localStorage.removeItem(QUIEN_KEY);
  } catch (_) {
    /* sin almacenamiento: se preguntará cada vez */
  }
}

/** Mismo avatar que en el panel: el backend envía una semilla equivalente al email sin exponerlo. */
function Avatar({ user, size = 44, className }) {
  return (
    <span className={cn("inline-flex shrink-0 rounded-full", className)}>
      <UserAvatar name={user?.avatar_seed || user?.nombre || "?"} variant={user?.avatar_variant} color={user?.color} size={size} />
    </span>
  );
}

const usuarioDeReserva = (r) => ({
  nombre: r.user_nombre,
  color: r.user_color,
  avatar_seed: r.user_avatar_seed,
  avatar_variant: r.user_avatar_variant,
});

/** Opciones rápidas de devolución. Cada una devuelve { fin: Date, dia_completo }. */
function opcionesDevolucion(now) {
  const enDosHoras = addHours(now, 2);
  enDosHoras.setMinutes(Math.ceil(enDosHoras.getMinutes() / 15) * 15, 0, 0);
  return [
    { id: "2h", label: "En 2 horas", sub: format(enDosHoras, "HH:mm"), fin: enDosHoras, dia_completo: false },
    { id: "hoy", label: "Hoy", sub: "al acabar el día", fin: new Date(endOfDay(now).getTime() + 1), dia_completo: true },
    { id: "manana", label: "Mañana", sub: format(addDays(now, 1), "EEE d", { locale: es }), fin: new Date(endOfDay(addDays(now, 1)).getTime() + 1), dia_completo: true },
    { id: "3d", label: "En 3 días", sub: format(addDays(now, 3), "EEE d", { locale: es }), fin: new Date(endOfDay(addDays(now, 3)).getTime() + 1), dia_completo: true },
  ];
}

export default function MaterialPublicPage() {
  const { etiqueta } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [quienId, setQuienId] = useState(leerQuien);
  const [cambiando, setCambiando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [opcion, setOpcion] = useState(null);
  const [otraFecha, setOtraFecha] = useState("");
  const [otraHora, setOtraHora] = useState("18:00");
  const [enSitio, setEnSitio] = useState(false);
  const [nota, setNota] = useState("");
  const [modo, setModo] = useState(null); // null | "devolver_ajeno"
  const [enviando, setEnviando] = useState(false);
  const [hecho, setHecho] = useState(null); // { tipo: "coger" | "devolver", texto }
  const [accionError, setAccionError] = useState(null);

  const cargar = useCallback(() => {
    return getMaterialPublic(etiqueta)
      .then((d) => {
        setData(d);
        setError(null);
        if (d.yo && !leerQuien()) {
          setQuienId(d.yo);
          guardarQuien(d.yo);
        }
      })
      .catch((e) => setError(getApiErrorMessage(e, "No se pudo cargar el equipo")));
  }, [etiqueta]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const usersById = useMemo(() => Object.fromEntries((data?.users || []).map((u) => [u.id, u])), [data]);
  const quien = quienId ? usersById[quienId] : null;
  const now = new Date();

  useEffect(() => {
    if (data && quienId && !usersById[quienId]) {
      setQuienId(null);
      guardarQuien(null);
    }
  }, [data, quienId, usersById]);

  if (error) {
    return (
      <Pantalla>
        <div className="flex flex-col items-center gap-3 py-24 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500" />
          <p className="text-lg font-semibold">{error}</p>
          <p className="text-sm text-muted-foreground">Avisa a un administrador para que revise la etiqueta.</p>
        </div>
      </Pantalla>
    );
  }
  if (!data) {
    return (
      <Pantalla>
        <div className="flex justify-center py-32">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Pantalla>
    );
  }

  const { item, actual, reservas } = data;
  const cfg = MATERIAL_ESTADOS[item.estado] || MATERIAL_ESTADOS.disponible;
  const loTengoYo = actual && actual.user_id === quienId;
  const loTieneOtro = actual && actual.user_id !== quienId;
  const reservadoAhoraPorOtro = !actual && item.estado === "reservado" && reservas.some((r) => r.user_id !== quienId && new Date(r.inicio) <= now);
  const proximaAjena = reservas.find((r) => r.user_id !== quienId && new Date(r.inicio) > now) || null;
  const limite = proximaAjena ? new Date(proximaAjena.inicio) : null;

  const opciones = opcionesDevolucion(now).map((o) => ({ ...o, deshabilitada: limite ? o.fin > limite : false }));
  const finOtra = otraFecha ? new Date(`${otraFecha}T${otraHora || "18:00"}`) : null;
  const finElegido = opcion === "otra" ? finOtra : opciones.find((o) => o.id === opcion)?.fin || null;
  const errorFin = !finElegido
    ? null
    : finElegido <= now
      ? "Elige un momento futuro"
      : limite && finElegido > limite
        ? `Tiene que ser antes del ${format(limite, "EEE d, HH:mm", { locale: es })}`
        : null;

  const elegirQuien = (id) => {
    setQuienId(id);
    guardarQuien(id);
    setCambiando(false);
    setBusqueda("");
  };

  const coger = async () => {
    if (!finElegido || errorFin) return;
    setEnviando(true);
    setAccionError(null);
    try {
      const diaCompleto = opcion === "otra" ? false : !!opciones.find((o) => o.id === opcion)?.dia_completo;
      const res = await materialPublicCoger(etiqueta, { user_id: quienId, fin: finElegido.toISOString(), dia_completo: diaCompleto });
      setHecho({
        tipo: "coger",
        texto: `${res.traspaso_de ? `Traspasado desde ${res.traspaso_de}. ` : ""}Devuélvelo${item.ubicacion ? ` a ${item.ubicacion}` : ""} ${
          diaCompleto ? `antes de que acabe el ${format(new Date(finElegido.getTime() - 1), "EEEE d", { locale: es })}` : `el ${format(finElegido, "EEEE d 'a las' HH:mm", { locale: es })}`
        }.`,
      });
      setOpcion(null);
      cargar();
    } catch (e) {
      setAccionError(getApiErrorMessage(e, "No se pudo registrar"));
    } finally {
      setEnviando(false);
    }
  };

  const devolver = async () => {
    setEnviando(true);
    setAccionError(null);
    try {
      await materialPublicDevolver(etiqueta, { user_id: quienId, nota_devolucion: nota });
      setHecho({ tipo: "devolver", texto: item.ubicacion ? `Gracias por dejarlo en ${item.ubicacion}.` : "¡Gracias!" });
      setNota("");
      setEnSitio(false);
      setModo(null);
      cargar();
    } catch (e) {
      setAccionError(getApiErrorMessage(e, "No se pudo registrar la devolución"));
    } finally {
      setEnviando(false);
    }
  };

  const usuariosFiltrados = (data.users || []).filter((u) => !busqueda.trim() || u.nombre.toLowerCase().includes(busqueda.trim().toLowerCase()));

  return (
    <Pantalla>
      {/* Equipo */}
      <div className="overflow-hidden rounded-3xl border bg-card shadow-sm">
        <div className="relative">
          {item.tiene_foto ? (
            <img src={getMaterialPublicFotoUrl(etiqueta, item.updated_at)} alt={item.nombre} className="aspect-[4/3] w-full object-cover" />
          ) : (
            <div className="flex aspect-[4/3] w-full items-center justify-center bg-gradient-to-br from-muted to-muted/40">
              <Camera className="h-14 w-14 text-muted-foreground/50" strokeWidth={1.25} />
            </div>
          )}
          <div className="absolute left-3 top-3">
            <MaterialEstadoBadge estado={item.estado} className="bg-white/95 text-sm shadow" />
          </div>
          <span className={cn("absolute inset-x-0 bottom-0 h-1.5", cfg.dot)} />
        </div>
        <div className="flex flex-col gap-1.5 p-5">
          <h1 className="text-2xl font-bold leading-tight">{item.nombre}</h1>
          {item.ubicacion && (
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" /> Se guarda en <span className="font-medium text-foreground">{item.ubicacion}</span>
            </p>
          )}
          {actual && (
            <div className={cn("mt-2 flex items-center gap-3 rounded-2xl px-3 py-2.5", item.estado === "retrasado" ? "bg-red-50" : "bg-muted/60")}>
              <Avatar user={usuarioDeReserva(actual)} size={36} />
              <div className="min-w-0 text-sm">
                <p className="font-semibold">{actual.user_id === quienId ? "Lo tienes tú" : `Lo tiene ${actual.user_nombre}`}</p>
                <p className={cn(item.estado === "retrasado" ? "font-medium text-red-600" : "text-muted-foreground")}>{formatVuelve(actual)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {hecho && (
        <div className="flex flex-col items-center gap-2 rounded-3xl border border-emerald-200 bg-emerald-50 px-5 py-6 text-center text-emerald-900">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white">
            <Check className="h-7 w-7" strokeWidth={3} />
          </span>
          <p className="text-lg font-semibold">{hecho.tipo === "coger" ? "¡Es tuyo! Buen rodaje" : "Devuelto"}</p>
          <p className="text-sm">{hecho.texto}</p>
          <Button variant="ghost" size="sm" className="mt-1" onClick={() => setHecho(null)}>
            Cerrar
          </Button>
        </div>
      )}

      {/* ¿Quién eres? */}
      {!hecho && (!quien || cambiando) && (
        <section className="flex flex-col gap-4 rounded-3xl border bg-card p-5 shadow-sm">
          <div>
            <h2 className="text-lg font-semibold">¿Quién eres?</h2>
            <p className="text-sm text-muted-foreground">Este móvil lo recordará para la próxima vez.</p>
          </div>
          {(data.users || []).length > 9 && (
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Busca tu nombre" className="pl-9" />
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            {usuariosFiltrados.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => elegirQuien(u.id)}
                className={cn(
                  "flex flex-col items-center gap-1.5 rounded-2xl border px-1 py-3 transition-colors active:scale-95 hover:bg-muted/60",
                  u.id === quienId && "border-primary bg-primary/5"
                )}
              >
                <Avatar user={u} size={48} />
                <span className="w-full truncate text-center text-xs font-medium">{u.nombre}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Acciones */}
      {!hecho && quien && !cambiando && (
        <section className="flex flex-col gap-4 rounded-3xl border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <Avatar user={quien} size={40} />
            <p className="flex-1 text-sm">
              Hola, <span className="font-semibold">{quien.nombre}</span>
            </p>
            <Button variant="ghost" size="sm" onClick={() => setCambiando(true)}>
              No soy yo
            </Button>
          </div>

          <Separator className="-mx-5 w-auto" />

          {item.estado === "reparacion" ? (
            <div className="flex items-center gap-3 rounded-2xl bg-zinc-100 px-4 py-3 text-sm text-zinc-700">
              <Wrench className="h-5 w-5 shrink-0" /> Este equipo está en reparación y no se puede usar.
            </div>
          ) : loTengoYo || modo === "devolver_ajeno" ? (
            <div className="flex flex-col gap-4">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <Undo2 className="h-5 w-5" /> {loTengoYo ? "Devolverlo" : `Devolverlo por ${actual.user_nombre}`}
              </h2>
              {item.ubicacion && (
                <label className="flex cursor-pointer items-center gap-3 rounded-2xl bg-primary/5 px-4 py-3 text-sm">
                  <Checkbox checked={enSitio} onCheckedChange={(v) => setEnSitio(v === true)} className="h-5 w-5" />
                  <span>
                    Lo he dejado en <span className="font-semibold">{item.ubicacion}</span>
                  </span>
                </label>
              )}
              <Textarea rows={2} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="¿Alguna incidencia? (opcional)" />
              <Button size="lg" className="h-12 gap-2 text-base" onClick={devolver} disabled={enviando || (item.ubicacion && !enSitio)}>
                {enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : <Undo2 className="h-5 w-5" />} Devolver
              </Button>
              {modo === "devolver_ajeno" && (
                <Button variant="ghost" onClick={() => setModo(null)}>
                  Volver
                </Button>
              )}
            </div>
          ) : reservadoAhoraPorOtro ? (
            <div className="flex items-start gap-3 rounded-2xl bg-sky-50 px-4 py-3 text-sm text-sky-900">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
              <span>
                Ahora mismo está reservado por <strong>{reservas.find((r) => r.user_id !== quienId && new Date(r.inicio) <= now)?.user_nombre}</strong>. Habla con esa persona antes de llevártelo.
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                {loTieneOtro ? <ArrowRightLeft className="h-5 w-5" /> : <Hand className="h-5 w-5" />}
                {loTieneOtro ? `Pásamelo (ahora lo tiene ${actual.user_nombre})` : "Me lo llevo"}
              </h2>
              <p className="-mt-2 text-sm text-muted-foreground">¿Hasta cuándo lo vas a tener?</p>

              {proximaAjena && (
                <div className="flex items-start gap-2 rounded-2xl bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Devuélvelo antes del <strong>{format(limite, "EEE d, HH:mm", { locale: es })}</strong>: lo tiene reservado {proximaAjena.user_nombre}.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                {opciones.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    disabled={o.deshabilitada}
                    onClick={() => setOpcion(o.id)}
                    className={cn(
                      "flex flex-col items-start rounded-2xl border px-4 py-3 text-left transition-all active:scale-[0.98]",
                      opcion === o.id ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "hover:bg-muted/60",
                      o.deshabilitada && "cursor-not-allowed opacity-40"
                    )}
                  >
                    <span className="font-semibold">{o.label}</span>
                    <span className="text-xs capitalize text-muted-foreground">{o.sub}</span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setOpcion("otra");
                    if (!otraFecha) setOtraFecha(format(startOfDay(now), "yyyy-MM-dd"));
                  }}
                  className={cn(
                    "col-span-2 rounded-2xl border px-4 py-3 text-left font-semibold transition-all",
                    opcion === "otra" ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "hover:bg-muted/60"
                  )}
                >
                  Otra fecha y hora
                </button>
              </div>

              {opcion === "otra" && (
                <div className="grid grid-cols-2 gap-2">
                  <Input type="date" value={otraFecha} min={format(now, "yyyy-MM-dd")} onChange={(e) => setOtraFecha(e.target.value)} />
                  <Input type="time" step={900} value={otraHora} onChange={(e) => setOtraHora(e.target.value)} />
                </div>
              )}
              {errorFin && <p className="text-sm text-red-600">{errorFin}</p>}

              <Button size="lg" className="h-12 gap-2 text-base" onClick={coger} disabled={enviando || !finElegido || !!errorFin}>
                {enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
                {loTieneOtro ? "Me lo paso" : "Me lo llevo"}
              </Button>

              {loTieneOtro && (
                <>
                  <Separator className="-mx-5 w-auto" />
                  <Button variant="outline" className="gap-2" onClick={() => setModo("devolver_ajeno")}>
                    <Undo2 className="h-4 w-4" /> No, solo lo devuelvo a su sitio
                  </Button>
                </>
              )}
            </div>
          )}

          {accionError && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{accionError}</p>}
        </section>
      )}

      {reservas.length > 0 && (
        <section className="flex flex-col gap-2 rounded-3xl border bg-card p-5 shadow-sm">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Próximas reservas</h2>
          {reservas.slice(0, 5).map((r, i) => (
            <div key={i} className="flex items-center gap-3 py-1 text-sm">
              <Avatar user={usuarioDeReserva(r)} size={28} />
              <span className="font-medium">{r.user_id === quienId ? "Tú" : r.user_nombre}</span>
              <span className="ml-auto text-right text-muted-foreground">{formatReservaRango(r)}</span>
            </div>
          ))}
        </section>
      )}
    </Pantalla>
  );
}

function Pantalla({ children }) {
  return (
    <div className="min-h-dvh bg-neutral-100">
      <div className="mx-auto flex max-w-md flex-col gap-4 px-4 py-5">
        {children}
        <p className="pb-4 pt-2 text-center text-xs text-muted-foreground">Material · Brutalco</p>
      </div>
    </div>
  );
}
